import { useLocation, Link } from 'react-router-dom';

import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { shouldShowMultiOrganizationControls } from 'src/contexts/OrganizationContext';

import { ReactComponent as GridIcon } from 'assets/icons/grid-2x2.svg';
import { ReactComponent as FolderBookmarkIcon } from 'assets/icons/folder-bookmark.svg';
import { ReactComponent as BellIcon } from 'assets/icons/bell.svg';
import { ReactComponent as BuildingIcon } from 'assets/icons/building-2.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg';
import { ReactComponent as PanelLeftIcon } from 'assets/icons/panel-left.svg';

const NAV_ITEM_CLASSES =
	'tw-flex tw-items-center tw-gap-2 tw-rounded tw-px-3 tw-py-4 tw-text-sm tw-font-bold tw-no-underline hover:tw-no-underline';

const DashboardNav = ({ toggleCollapse, isCollapsed }) => {
	const isMobile = useMediaPredicate(MOBILE_SIZE);

	const location = useLocation();
	const pathname = location.pathname;

	// VWA-2752: the Organizations screen exists to compare and move between
	// organizations. A user who belongs to exactly one has nothing to compare
	// or switch to, so the link is hidden for them. The list is loaded once by
	// OrganizationProvider (src/contexts/OrganizationContext) at the app root;
	// this only reads it, and reads an empty list while that fetch is in
	// flight, which keeps the link hidden until the real count is known.
	// A list that FAILED to load is not a count of one: the shared rule keeps
	// the link visible in that case rather than hiding navigation silently.
	const { items: organizations, error: organizationsError } =
		useOrganizations();
	const belongsToMultipleOrganizations = shouldShowMultiOrganizationControls({
		items: organizations,
		error: organizationsError,
	});

	const isActive = (navItem) => {
		return pathname === navItem.url;
	};

	const navItems = [
		{
			label: 'Dashboard',
			url: '/dashboard/overview',
			key: 'dashboard',
			Icon: GridIcon,
		},
		{
			label: 'Portfolio',
			url: '/dashboard/portfolio',
			key: 'portfolio',
			Icon: FolderBookmarkIcon,
		},
		{
			label: 'Alerts',
			url: '/dashboard/alerts',
			key: 'alerts',
			Icon: BellIcon,
		},
		...(belongsToMultipleOrganizations
			? [
					{
						label: 'Organizations',
						url: '/dashboard/organizations',
						key: 'organizations',
						Icon: BuildingIcon,
					},
				]
			: []),
		{
			label: 'Settings',
			url: '/dashboard/settings',
			key: 'settings',
			Icon: SettingsIcon,
		},
	];

	const collapsed = isCollapsed || isMobile;

	return (
		<div
			className={`violerts-v3-root tw-flex tw-h-full tw-flex-shrink-0 tw-flex-col tw-justify-between tw-overflow-hidden tw-border-r tw-border-border-01 tw-bg-surface-layer-01 tw-px-4 tw-py-6 tw-transition-[width] tw-duration-300 tw-ease-in-out ${
				collapsed ? 'tw-w-[80px]' : 'tw-w-[300px]'
			}`}
		>
			<div className="tw-flex tw-flex-col tw-items-stretch">
				{navItems.map(({ Icon, ...item }) => (
					<Link
						key={item.key}
						to={item.url}
						className={`${NAV_ITEM_CLASSES} ${
							isActive(item)
								? 'tw-bg-surface-layer-03 tw-text-surface-background hover:tw-text-surface-background'
								: 'tw-text-text-primary hover:tw-bg-surface-layer-02 hover:tw-text-text-primary'
						}`}
					>
						<Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" aria-hidden="true" />
						{!collapsed && <span>{item.label}</span>}
					</Link>
				))}
			</div>
			{!isMobile && (
				<button
					type="button"
					onClick={toggleCollapse}
					aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					className={`${NAV_ITEM_CLASSES} tw-border tw-border-solid tw-border-border-01 tw-bg-surface-background tw-text-text-primary hover:tw-bg-surface-layer-02 ${
						collapsed ? 'tw-justify-center' : ''
					}`}
					style={{ border: '1px solid #C4C4C4' }}
				>
					<PanelLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" aria-hidden="true" />
					{!isCollapsed && <span>Collapse panel</span>}
				</button>
			)}
		</div>
	);
};

export default DashboardNav;
